import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./HourlyGallery.css";
import { FaEye, FaCloudUploadAlt } from "react-icons/fa";
import Loader2 from "../../../../Template/Loader/Loader2";

const HourlyGallery = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  
  // State for tabs
  const [activeCategory, setActiveCategory] = useState("Standard"); // 'Standard' | 'Premium'
  const [activeHours, setActiveHours] = useState("0-3 Hours"); // '0-3 Hours' | '5 Hours' | '8 Hours'
  
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const stored = sessionStorage.getItem("isSidebarOpen");
    return stored !== null ? JSON.parse(stored) : true;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const stored = sessionStorage.getItem("isSidebarOpen");
      const parsed = stored !== null ? JSON.parse(stored) : true;

      if (parsed !== isSidebarOpen) {
        setIsSidebarOpen(parsed);
      }
    }, 10);

    return () => clearInterval(interval);
  }, [isSidebarOpen]);

  // Mock booking data based on structure (Handles multiple packages)
  const galleryBookingData = {
    clientName: "Rahul Shah",
    eventAddress: "Mumbai, Maharashtra",
    bookingId: bookingId || "BK001",
    hourlyPackages: [
      {
        category: "Standard",
        hours: "0-3 Hours",
        services: [
          { name: "Photographer", qty: 2, price: 8000 },
          { name: "Videographer", qty: 1, price: 5000 },
        ],
      },
      {
        category: "Premium",
        hours: "8 Hours",
        services: [
          { name: "Photographer", qty: 1, price: 5000 },
          { name: "Editing", qty: 2, price: 3000 },
        ],
      }
    ]
  };

  const handleBack = () => navigate(-1);

  const handleViewGallery = () => {
    navigate(`/bookings/hourly-view-gallery/${bookingId}`);
  };

  const handleFileSelect = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setFiles(Array.from(e.dataTransfer.files));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Find the package that matches both category and hours
  const activePkg = galleryBookingData.hourlyPackages.find(
    (pkg) => pkg.category === activeCategory && pkg.hours === activeHours
  );

  // Simulated Frontend-only Upload
  const handleSubmitUpload = () => {
    if (files.length === 0 || !activePkg) return;

    setUploading(true);
    setUploadProgress(0);

    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadProgress(progress);
      if (progress >= 100) {
        clearInterval(interval);
        
        // Save to localStorage specifically for this package
        const packageKey = `${activePkg.category}_${activePkg.hours}`.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
        
        const newFiles = files.map((file, idx) => ({
          id: Date.now() + idx,
          key: file.name,
          url: URL.createObjectURL(file), 
          name: file.name,
          packageKey: packageKey,
          type: file.type.startsWith("video") ? "Video" : "Photo"
        }));

        const storageKey = `hourly_files_${bookingId || 'default'}`;
        const existing = JSON.parse(localStorage.getItem(storageKey)) || [];
        const updated = [...existing, ...newFiles];
        localStorage.setItem(storageKey, JSON.stringify(updated));

        setTimeout(() => {
          setUploading(false);
          setFiles([]);
          alert("Upload complete! You can now see these in the View Gallery.");
        }, 500);
      }
    }, 150);
  };

  if (uploading) return <Loader2 percentage={uploadProgress} statusText="Simulating Upload..." />;

  return (
    <div
      className={`content-container ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
      style={{ marginTop: "100px" }}
    >
      <div className="page-inner-wrapper">
        <h2 className="mb-4">Hourly Gallery Upload</h2>
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="gallery-container">
              
              {/* HEADER */}
              <div className="gallery-header">
                <button onClick={handleBack} className="admin-back-btn">
                  ← Back
                </button>
                <button onClick={handleViewGallery} className="admin-back-btn">
                  <FaEye style={{ marginRight: "6px" }} />
                  View Gallery
                </button>
              </div>

              {/* BOOKING INFO */}
              <h2>{galleryBookingData.clientName}</h2>
              <p className="text-muted mb-1">Event Location - {galleryBookingData.eventAddress}</p>
              <p className="text-muted">Booking ID - {galleryBookingData.bookingId}</p>

              {/* TOP TABS: Categories */}
              <div className="custom-tabs mb-2 mt-4">
                {["Standard", "Premium"].map((cat) => (
                  <div
                    key={cat}
                    className={`tab-item ${activeCategory === cat ? "active" : ""}`}
                    onClick={() => setActiveCategory(cat)}
                  >
                    {cat}
                  </div>
                ))}
              </div>

              {/* MIDDLE TABS: Hours */}
              <div className="custom-tabs sub-tabs mb-4">
                {["0-3 Hours", "5 Hours", "8 Hours"].map((hr) => (
                  <div
                    key={hr}
                    className={`tab-item ${activeHours === hr ? "active" : ""}`}
                    onClick={() => setActiveHours(hr)}
                  >
                    {hr}
                  </div>
                ))}
              </div>

              {/* UPLOAD BOX */}
              {activePkg ? (
                <div 
                  className="upload-box"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                >
                  <FaCloudUploadAlt size={48} className="mb-3" style={{ color: "var(--primary-color)" }} />
                  <p>Drag and Drop photos or videos here for <strong>{activeCategory} ({activeHours})</strong></p>
                  <span>OR</span>
                  <div className="d-flex justify-content-center gap-2">
                    <label className="upload-btn">
                      Select Files
                      <input
                        type="file"
                        hidden
                        multiple
                        accept="image/*,video/*"
                        onChange={handleFileSelect}
                      />
                    </label>
                    <label className="upload-btn">
                      Select Folder
                      <input
                        type="file"
                        hidden
                        multiple
                        webkitdirectory="true"
                        onChange={handleFileSelect}
                      />
                    </label>
                  </div>

                  {files.length > 0 && (
                    <button 
                      className="upload-btn mt-4" 
                      style={{ background: "var(--primary-color)", color: "#fff", borderColor: "var(--primary-color)" }}
                      onClick={handleSubmitUpload}
                    >
                      Upload Files ({files.length})
                    </button>
                  )}
                </div>
              ) : (
                 <div className="text-center py-5 border rounded bg-light">
                  <p className="text-muted m-0">This package does not exist for this booking.</p>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HourlyGallery;
